import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Volume2, Play, Star, Users, AlertTriangle, ExternalLink, CheckCircle } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const BlogPost3: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleGeneratePracticeName = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      navigate('/chinese-name-generator', { 
        state: { 
          firstName: firstName.trim(),
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };
  const toneExamples = [
    { tone: '1st Tone (高平)', symbol: 'ā', description: 'High and flat', example: '妈 (mā) - mother' },
    { tone: '2nd Tone (上升)', symbol: 'á', description: 'Rising', example: '麻 (má) - hemp' },
    { tone: '3rd Tone (低降升)', symbol: 'ǎ', description: 'Low dip', example: '马 (mǎ) - horse' },
    { tone: '4th Tone (下降)', symbol: 'à', description: 'Falling', example: '骂 (mà) - to scold' }
  ];

  const commonSurnames = [
    { chinese: '李', pinyin: 'Lǐ', english: 'Lee', difficulty: 'Easy' },
    { chinese: '王', pinyin: 'Wáng', english: 'Wang', difficulty: 'Easy' },
    { chinese: '张', pinyin: 'Zhāng', english: 'Zhang', difficulty: 'Medium' },
    { chinese: '刘', pinyin: 'Liú', english: 'Liu', difficulty: 'Medium' },
    { chinese: '陈', pinyin: 'Chén', english: 'Chen', difficulty: 'Easy' },
    { chinese: '杨', pinyin: 'Yáng', english: 'Yang', difficulty: 'Easy' }
  ];

  return (
    <>
      <SeoMeta
        title="How to Pronounce Chinese Names: Complete Guide with Audio Examples"
        description="Afraid of mispronouncing Chinese names? Master the 4 tones and pinyin system with our beginner-friendly guide. Learn why tone matters (mā vs má vs mǎ vs mà), common pronunciation mistakes English speakers make, and practice with audio examples. Includes pronunciation tips for popular names, tone combinations, and how to sound natural. Stop embarrassing mispronunciations and speak Chinese names confidently."
        keywords="how to pronounce chinese names, pronounce chinese names, chinese name pronunciation guide, chinese names pronunciation for english speakers, how do you pronounce chinese names, chinese surname pronunciation, pinyin pronunciation for names, chinese name pronunciation rules, common chinese names pronunciation, chinese name pronunciation audio, learn chinese name pronunciation"
        pathname="/blog/chinese-names-pronunciation"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>

        {/* Article Header */}
        <header className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <Volume2 className="w-4 h-4" />
              Pronunciation Guide
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              How to Pronounce Chinese Names Correctly: Master Guide
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Master Chinese name pronunciation with our comprehensive guide. Learn pinyin basics, tone marks, and common pronunciation mistakes. Includes audio examples and practice exercises for English speakers.
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
              <span>Published: June 12, 2025</span>
              <span>12 min read</span>
              <span>Audio Examples Included</span>
            </div>

            {/* Interactive Pronunciation Practice Generator */}
            <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white mb-12">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Volume2 className="w-5 h-5" />
                Practice Chinese Name Pronunciation
              </h3>
              <form onSubmit={handleGeneratePracticeName} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-white/90 text-gray-900 placeholder-gray-500"
                      placeholder="Your first name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Last Name</label>
                    <input
                      type="text"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-white/90 text-gray-900 placeholder-gray-500"
                      placeholder="Your last name"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!firstName.trim() || !surname.trim()}
                  className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2 disabled:bg-gray-200 disabled:text-gray-400"
                >
                  Generate Practice Names
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <article className="px-4 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              
              {/* Table of Contents */}
              <div className="bg-green-50 rounded-xl p-6 mb-8">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-green-600" />
                  Pronunciation Mastery Plan
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Understanding pinyin for Chinese names</li>
                  <li>• The four tones in Chinese name pronunciation</li>
                  <li>• Common Chinese surnames pronunciation guide</li>
                  <li>• Practice exercises for English speakers</li>
                </ul>
              </div>

              {/* Section 1 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">1</div>
                  Understanding Pinyin for Chinese Names
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Learning <strong>how to pronounce chinese names</strong> starts with understanding pinyin - the official romanization system for Standard Chinese. This system is essential for English speakers learning <strong>chinese name pronunciation</strong>.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
                  <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Basic Pinyin Rules for Names
                  </h4>
                  <ul className="text-blue-700 space-y-2">
                    <li>• <strong>Each syllable = one character</strong> in Chinese names</li>
                    <li>• <strong>Tone marks</strong> indicate pitch changes (crucial for meaning)</li>
                    <li>• <strong>Initial sounds</strong> may differ from English pronunciation</li>
                    <li>• <strong>Final sounds</strong> often end in vowels or -n, -ng sounds</li>
                  </ul>
                </div>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  For <strong>chinese names pronunciation for english speakers</strong>, the key is understanding that Chinese is a tonal language. The same syllable with different tones can have completely different meanings.
                </p>

                <div className="bg-yellow-50 rounded-xl p-6 mb-8">
                  <h4 className="font-bold text-yellow-800 mb-3">Common Pronunciation Challenges</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-yellow-700 mb-2">For English Speakers:</h5>
                      <ul className="text-yellow-600 text-sm space-y-1">
                        <li>• Zh-, Ch-, Sh- sounds</li>
                        <li>• X- (like "sh" + "ee")</li>
                        <li>• Q- (like "ch" + "ee")</li>
                        <li>• Final -iang, -uang sounds</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-yellow-700 mb-2">Tone Recognition:</h5>
                      <ul className="text-yellow-600 text-sm space-y-1">
                        <li>• Distinguishing rising vs falling</li>
                        <li>• Third tone dip pattern</li>
                        <li>• Neutral tone variations</li>
                        <li>• Tone sandhi in compound names</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Practice CTA */}
                <div className="bg-purple-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-purple-800 mb-2">Practice Real Chinese Names</h4>
                      <p className="text-purple-700">Generate names with detailed pronunciation guides</p>
                    </div>
                    <Link 
                      to="/chinese-name-generator"
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      Practice Now
                      <Play className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Section 2 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">2</div>
                  The Four Tones in Chinese Name Pronunciation
                </h2>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Understanding <strong>how to read tone marks</strong> is fundamental to <strong>chinese name pronunciation rules</strong>. Each tone changes the meaning of the syllable completely.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {toneExamples.map((tone, index) => (
                    <div key={index} className="bg-green-50 rounded-xl p-6 border border-green-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-bold text-green-800">{tone.tone}</h4>
                        <span className="text-2xl font-bold text-green-600">{tone.symbol}</span>
                      </div>
                      <p className="text-green-700 mb-2">{tone.description}</p>
                      <p className="text-gray-600 text-sm bg-white p-2 rounded">{tone.example}</p>
                      <button className="mt-2 flex items-center gap-2 text-green-600 hover:text-green-700 text-sm">
                        <Volume2 className="w-4 h-4" />
                        Listen (Audio coming soon)
                      </button>
                    </div>
                  ))}
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mb-6">
                  <h4 className="font-bold text-orange-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Tone Tips for Chinese Name Pronunciation
                  </h4>
                  <ul className="text-orange-700 space-y-2">
                    <li>• <strong>Start with surname tones</strong> - practice the family name first</li>
                    <li>• <strong>Listen to native speakers</strong> - tone patterns vary by region</li>
                    <li>• <strong>Practice tone pairs</strong> - how tones interact in compound names</li>
                    <li>• <strong>Don't worry about perfection</strong> - context helps understanding</li>
                  </ul>
                </div>
              </section>

              {/* Section 3 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">3</div>
                  Common Chinese Surnames Pronunciation Guide
                </h2>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Let's focus on <strong>chinese surname pronunciation</strong> and <strong>common chinese names pronunciation</strong>. These surnames represent about 40% of the Chinese population:
                </p>

                <div className="grid gap-4 mb-8">
                  {commonSurnames.map((surname, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className="text-3xl font-bold text-gray-900 w-12 text-center">{surname.chinese}</div>
                        <div>
                          <h4 className="font-bold text-gray-900">{surname.pinyin}</h4>
                          <p className="text-gray-600 text-sm">English: {surname.english}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          surname.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                          surname.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {surname.difficulty}
                        </span>
                        <button className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm">
                          <Volume2 className="w-4 h-4" />
                          Listen
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-teal-50 rounded-xl p-6 mb-8">
                  <h4 className="font-bold text-teal-800 mb-3">Pronunciation Memory Tips</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-teal-700 mb-2">Visual Association:</h5>
                      <ul className="text-teal-600 text-sm space-y-1">
                        <li>• 李 (Lǐ) - like "Lee" with a small dip</li>
                        <li>• 王 (Wáng) - "Wong" with rising tone</li>
                        <li>• 张 (Zhāng) - "Jong" with high flat tone</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-teal-700 mb-2">Common Mistakes:</h5>
                      <ul className="text-teal-600 text-sm space-y-1">
                        <li>• Zhang ≠ "Zhang" (English Z sound)</li>
                        <li>• Liu ≠ "Loo" (contains "i" glide)</li>
                        <li>• Chen ≠ "Chen" (Ch + en, not tch)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Surnames Learning Link */}
                <div className="bg-blue-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-blue-800 mb-2">Master Chinese Surnames</h4>
                      <p className="text-blue-700">Explore comprehensive surname meanings and pronunciations</p>
                    </div>
                    <Link 
                      to="/chinese-last-names"
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      Learn Surnames
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">4</div>
                  Practice Exercises for English Speakers
                </h2>

                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Here are structured exercises to improve your <strong>chinese name pronunciation audio</strong> skills and <strong>learn chinese name pronunciation</strong> effectively:
                </p>

                <div className="space-y-6 mb-8">
                  <div className="bg-purple-50 rounded-xl p-6">
                    <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Exercise 1: Tone Practice
                    </h4>
                    <div className="grid md:grid-cols-4 gap-3">
                      <div className="bg-white p-3 rounded text-center">
                        <div className="text-lg font-bold text-gray-900">mā</div>
                        <div className="text-sm text-gray-600">High flat</div>
                      </div>
                      <div className="bg-white p-3 rounded text-center">
                        <div className="text-lg font-bold text-gray-900">má</div>
                        <div className="text-sm text-gray-600">Rising</div>
                      </div>
                      <div className="bg-white p-3 rounded text-center">
                        <div className="text-lg font-bold text-gray-900">mǎ</div>
                        <div className="text-sm text-gray-600">Low dip</div>
                      </div>
                      <div className="bg-white p-3 rounded text-center">
                        <div className="text-lg font-bold text-gray-900">mà</div>
                        <div className="text-sm text-gray-600">Falling</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-xl p-6">
                    <h4 className="font-bold text-orange-800 mb-3">Exercise 2: Name Combinations</h4>
                    <p className="text-orange-700 mb-3">Practice these common name patterns:</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="text-orange-600 space-y-2">
                        <li>• 李明 (Lǐ Míng) - 3rd + 2nd tone</li>
                        <li>• 王雅 (Wáng Yǎ) - 2nd + 3rd tone</li>
                        <li>• 张伟 (Zhāng Wěi) - 1st + 3rd tone</li>
                      </ul>
                      <ul className="text-orange-600 space-y-2">
                        <li>• 刘欣 (Liú Xīn) - 2nd + 1st tone</li>
                        <li>• 陈好 (Chén Hǎo) - 2nd + 3rd tone</li>
                        <li>• 杨帆 (Yáng Fān) - 2nd + 1st tone</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-xl p-6">
                    <h4 className="font-bold text-green-800 mb-3">Exercise 3: Regional Variations</h4>
                    <p className="text-green-700 mb-3">Understanding <strong>regional pronunciation differences</strong>:</p>
                    <ul className="text-green-600 space-y-2">
                      <li>• <strong>Northern China</strong>: Clear retroflex sounds (zh, ch, sh)</li>
                      <li>• <strong>Southern China</strong>: May merge some initials (z/zh, c/ch, s/sh)</li>
                      <li>• <strong>Taiwan</strong>: Slight tone differences, especially 3rd tone</li>
                      <li>• <strong>International</strong>: Simplified pronunciation for clarity</li>
                    </ul>
                  </div>
                </div>

                {/* Audio Resources Note */}
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
                  <h4 className="font-bold text-yellow-800 mb-2">Audio Practice Resources</h4>
                  <p className="text-yellow-700">
                    For the best <strong>chinese name pronunciation audio</strong> practice, we recommend using our name generator which provides accurate pinyin and pronunciation guides for all generated names.
                  </p>
                </div>

                {/* Practice CTA */}
                <div className="bg-green-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-green-800 mb-2">Interactive Pronunciation Practice</h4>
                      <p className="text-green-700">Generate names and practice pronunciation with pinyin guides</p>
                    </div>
                    <Link 
                      to="/chinese-name-generator"
                      className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      Start Practicing
                      <Volume2 className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Conclusion CTA */}
              <section className="mb-8">
                <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-8 text-white text-center">
                  <h3 className="text-2xl font-bold mb-4">Master Chinese Name Pronunciation</h3>
                  <p className="text-lg opacity-90 mb-6">
                    Practice with our AI-generated names featuring accurate pinyin pronunciation guides and cultural context.
                  </p>
                  <Link 
                    to="/chinese-name-generator"
                    className="inline-flex items-center gap-2 bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    Practice Pronunciation
                    <Volume2 className="w-5 h-5" />
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>

        {/* Navigation Footer */}
        <footer className="py-12 px-4 bg-white border-t">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link 
                to="/blog/chinese-names-meanings"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous: Chinese Names & Meanings
              </Link>
              
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium"
              >
                Back to Blog Home
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPost3; 
